// Les modules d'Arceau (D-122, révise D-121 ; rangés par objectifs, D-132) : le socle est toujours là ; chaque module est
// activé par le bureau (« Ce que le club utilise » selon ses besoins, Club › Modules, la ligne « Activer » de chaque
// objectif dans l'onglet Club). Ici, ce qui ne dépend que des données : la liste et ses textes — pourquoi l'activer (des
// résultats, pas des fonctions), le temps de mise en place, ce que ça demande au bureau, pour qui, ce qui apparaît rôle
// par rôle, ce que l'extinction fait disparaître ou garde —, les objectifs du club, les besoins de l'activation, la page
// d'un module, les onglets du bureau. Logique pure, testée (modules.test.ts). Tutoiement ; accords via agree()
// (« Joueuses », « grande / plus jeune » d'après le genre des équipes du club).
import { agree, type TeamGender } from './labels';

export type ModuleKey = 'benevolat' | 'licences' | 'page' | 'partenaires' | 'agenda' | 'avantages' | 'defis' | 'prepa' | 'parrainage' | 'subventions' | 'salles' | 'tournois' | 'boutique' | 'buvette' | 'tresorerie' | 'formation' | 'skins';

/** Les modules ouverts à tous les clubs (activation, page de vente, besoins). */
export const MODULE_KEYS: ModuleKey[] = ['benevolat', 'licences', 'page', 'partenaires', 'agenda', 'avantages', 'defis', 'prepa', 'parrainage', 'subventions', 'salles', 'tournois', 'boutique', 'buvette', 'tresorerie'];
/** Les modules en bêta (D-141) : seulement pour un club de la bêta (indicateur de la console, D-081 ; la démo). Ailleurs, ils
 *  n'apparaissent nulle part. */
export const BETA_MODULES: ModuleKey[] = ['formation'];
/** Les modules allumés par défaut (D-149) : sans réglage du bureau, ils sont là (la base : module_on) ; le bureau peut les
 *  éteindre dans Club › Modules. Ni dans « Ce que le club utilise », ni dans les besoins : rien à mettre en place. */
export const DEFAULT_ON: ModuleKey[] = ['skins'];
/** Tous les modules, dans l'ordre de la base (_module_keys). */
export const ALL_MODULE_KEYS: ModuleKey[] = [...MODULE_KEYS, ...BETA_MODULES, ...DEFAULT_ON];

// --- Les objectifs du club (D-132) ------------------------------------------------------------------------------------
export type GoalKey = 'jouer' | 'mobiliser' | 'vivre' | 'financer' | 'adherents' | 'recruter' | 'arceau';
export interface Goal {
  key: GoalKey;
  title: string;
  /** Ce que le socle y met déjà (Club › Modules). */
  base?: string;
  /** Ses modules, dans l'ordre de la maquette m3. */
  modules: ModuleKey[];
}
/** Dans l'ordre de D-132 et de l'onglet Club du bureau (maquette m6), le même partout. */
export const GOALS: Goal[] = [
  { key: 'jouer', title: 'Faire jouer', base: 'Équipes, planning, matchs et convocations : toujours là.', modules: ['salles', 'prepa'] },
  { key: 'mobiliser', title: 'Mobiliser les bénévoles', modules: ['benevolat'] },
  { key: 'vivre', title: 'Faire vivre le club', modules: ['defis', 'parrainage', 'avantages', 'agenda', 'tournois'] },
  { key: 'financer', title: 'Financer le club', modules: ['partenaires', 'subventions', 'boutique', 'buvette', 'tresorerie'] },
  { key: 'adherents', title: 'Gérer les adhérents', modules: ['licences'] },
  { key: 'recruter', title: 'Se montrer et recruter', modules: ['page'] },
  { key: 'arceau', title: 'Le club et Arceau', modules: [] },
];
export const GOAL: Record<GoalKey, Goal> = Object.fromEntries(GOALS.map(g => [g.key, g])) as Record<GoalKey, Goal>;

/** Un bénéfice : un picto, ce que le club y gagne, en une ligne de plus. */
export interface Why { icon: string; title: string; text: string }

export interface ModuleInfo {
  key: ModuleKey;
  /** Son nom, le même partout (Club › Modules, fiches, aide). */
  title: string;
  /** Son nom court, sur une tuile, un onglet ou une ligne « ⚙ Réglages » (« Parrainage », « Agenda »). */
  short: string;
  /** Dans une phrase (« Activer le bénévolat », « Éteindre les avantages »). */
  noun: string;
  /** Son icône, une seule, partout (D-132 : une icône = un sens). */
  icon: string;
  /** Son objectif (D-132). */
  goal: GoalKey;
  /** Pourquoi l'activer, en une phrase : un résultat, pas une fonction (lignes des modules, « Activer » d'un objectif). */
  sub: (g: TeamGender) => string;
  /** Pourquoi l'activer : ses bénéfices (fiche « Activer », activation selon les besoins). */
  why: (g: TeamGender) => Why[];
  /** La mise en place : les minutes pour démarrer (la pastille ⏱) et ce qu'elle comprend. */
  setup: { min: number; text: string };
  /** Ce que ça demande au bureau, ensuite. */
  asks: string;
  /** Pour qui (pastilles). */
  who: (g: TeamGender) => string[];
  /** Le film du tuto filmé sur la démo (public/guide/<tuto>.webp), s'il y en a un. */
  film?: string;
  /** Réservé au bureau : pas d'annonce « Prévenir les membres ». */
  bureauOnly?: boolean;
  /** Ce qui apparaît, rôle par rôle (fiche « Activer »). */
  appears: (g: TeamGender) => [string, string][];
  /** Ce qui disparaît (fiche « Éteindre »). */
  hides: string;
  /** Ce qui est gardé, tout revient tel quel. */
  keeps: string;
  /** Un module en bêta (D-141) : sa pastille (« Bêta · club pilote ») et ce qu'il coûte. */
  beta?: { label: string; price: string };
}

export const MODULES: Record<ModuleKey, ModuleInfo> = {
  benevolat: {
    key: 'benevolat', title: 'Bénévolat', short: 'Bénévolat', noun: 'le bénévolat', icon: 'ti-heart', goal: 'mobiliser', film: 'demander-benevoles',
    sub: () => 'Mobiliser les bénévoles en 1 clic, les motiver et les remercier.',
    why: () => [
      { icon: '📣', title: 'Mobiliser en 1 clic', text: 'Un créneau vide : les bons bénévoles sont prévenus, chacun prend sa place en un geste.' },
      { icon: '🔥', title: 'Motiver', text: 'Parcours, grades et défis entre groupes de bénévoles.' },
      { icon: '🤝', title: 'Créer de la cohésion', text: 'Des groupes avec leur nom, leur emoji et leur chef : les cuistos, les gros bras…' },
      { icon: '🎁', title: 'Reconnaître', text: '« Bravo à… » chaque lundi, récompenses et cadeaux du club.' },
    ],
    setup: { min: 5, text: 'les créneaux des matchs à domicile se créent tout seuls' },
    asks: 'Valider une fois les besoins par équipe, relancer quand il manque du monde (un bouton). Le rappel de la veille et le « Bravo » partent seuls.',
    who: () => ['Bureau', 'Membres adultes', 'Coachs'],
    appears: () => [
      ['Bureau', 'L’onglet Bénévolat : créneaux, besoins des matchs, groupes, badges.'],
      ['Membres', 'La casquette Bénévole et les créneaux à prendre (adultes ; les jeunes, seulement ce que tu leur ouvres).'],
      ['Coachs', 'Les bénévoles de leurs matchs à domicile.'],
    ],
    hides: 'L’onglet Bénévolat, la casquette Bénévole, les besoins des prochains matchs, les relances.',
    keeps: 'Les points, badges, groupes et réglages : tout revient tel quel si tu le rallumes.',
  },
  licences: {
    key: 'licences', title: 'Licences et dossiers', short: 'Licences', noun: 'les licences et dossiers', icon: 'ti-id', goal: 'adherents', film: 'dossiers-licence',
    sub: () => 'Des dossiers complets sans relancer à la main.',
    why: () => [
      { icon: '✅', title: 'Tu sais qui peut jouer', text: 'Licence FFBB, médical, pièces, cotisation : dossier par dossier, d’un coup d’œil.' },
      { icon: '📨', title: 'Fini les relances une par une', text: 'Un bouton : chaque famille reçoit seulement ce qui lui manque.' },
      { icon: '💶', title: 'Les cotisations se tiennent', text: 'Paiement en plusieurs fois, chèques à déposer, retards visibles.' },
      { icon: '📥', title: 'Rien à ressaisir', text: 'L’export FBI s’importe, le tarif suit l’équipe.' },
    ],
    setup: { min: 30, text: 'en début de saison : import FBI, tarifs par équipe, pièces demandées' },
    asks: 'Noter les étapes FFBB et les paiements reçus ; relancer en un bouton.',
    who: () => ['Bureau', 'Familles', 'Coachs'],
    appears: () => [
      ['Bureau', 'L’onglet Licences : les dossiers de la saison, les pièces, les paiements, les relances.'],
      ['Familles', 'Le dossier de chaque licencié sur l’accueil : ce qui manque, l’échéancier.'],
      ['Coachs', 'Qui est licencié dans leur effectif.'],
    ],
    hides: 'L’onglet Licences, les dossiers sur l’accueil des familles, les relances.',
    keeps: 'Les dossiers, les pièces demandées, les paiements et les tarifs : tout revient tel quel.',
  },
  page: {
    key: 'page', title: 'Page du club', short: 'Page du club', noun: 'la page du club', icon: 'ti-globe', goal: 'recruter', film: 'page-publique',
    sub: () => 'Être trouvé : matchs, résultats, contact, en ligne.',
    why: () => [
      { icon: '🌐', title: 'Ta vitrine, à jour toute seule', text: 'Matchs, résultats, équipes et contact, depuis la FFBB.' },
      { icon: '🙋', title: 'Tu recrutes sans formulaire bricolé', text: '« Viens essayer » arrive au bureau et au coach de la catégorie.' },
      { icon: '⭐', title: 'Les familles suivent leur équipe', text: 'Le résultat sur leur téléphone, sans compte au club.' },
      { icon: '📊', title: 'Des chiffres pour tes partenaires', text: 'Les abonnés et les visites comptent dans la plaquette.' },
    ],
    setup: { min: 10, text: 'adresse, accroche, réseaux, mentions légales' },
    asks: 'Répondre aux demandes d’essai. La page se met à jour seule.',
    who: () => ['Public', 'Bureau'],
    appears: () => [
      ['Public', 'La page du club en ligne : matchs, résultats, équipes, contact ; aucune donnée personnelle.'],
      ['Bureau', 'Les essais à recontacter, la page et ses réseaux.'],
      ['Membres', '« ☆ Suivre » une équipe : ses résultats sur le téléphone.'],
    ],
    hides: 'La page du club (hors ligne), « Viens essayer », « Suivre » et ses e-mails.',
    keeps: 'Les textes, les réseaux, l’adresse et les abonnés : la page revient telle quelle.',
  },
  partenaires: {
    key: 'partenaires', title: 'Partenaires', short: 'Partenaires', noun: 'les partenaires', icon: 'ti-handshake', goal: 'financer', film: 'partenaires',
    sub: () => 'Trouver des partenaires et les garder, sans leur courir après.',
    why: () => [
      { icon: '📈', title: 'Ils voient ce que ça leur rapporte', text: 'Affichages, clics, matchs présentés : un bilan pour renouveler.' },
      { icon: '🤝', title: 'Chaque famille devient apporteur', text: 'Un lien pour son employeur ou son commerçant ; une mission Ambassade si ça signe.' },
      { icon: '🧭', title: 'Les entreprises autour du gymnase', text: 'Un suivi simple et une plaquette aux vrais chiffres du club.' },
      { icon: '💌', title: 'La relation tient toute l’année', text: 'Une attention par trimestre, sans demander d’argent.' },
    ],
    setup: { min: 15, text: 'tes partenaires actuels d’abord (nom, logo, niveau) ; leurs formules et leur invitation ensuite' },
    asks: 'Valider ce que proposent les partenaires (bannière, offre), tenir la prospection, relire les nouvelles du trimestre.',
    who: () => ['Bureau', 'Partenaires'],
    appears: () => [
      ['Bureau', 'Les partenaires et leurs contrats, la prospection des entreprises autour du gymnase.'],
      ['Membres', 'Les partenaires du club et leurs offres (adultes), « Qui connaît ? ».'],
      ['Partenaires', 'Leur espace, sur invitation : visibilité, appels du club.'],
    ],
    hides: 'Les partenaires dans l’app, « Match présenté par », la prospection, « Qui connaît ? ».',
    keeps: 'Les fiches, contrats et pistes : tout revient tel quel.',
  },
  agenda: {
    key: 'agenda', title: 'Agenda du club', short: 'Agenda', noun: 'l’agenda du club', icon: 'ti-ticket', goal: 'vivre', film: 'agenda',
    sub: () => 'Remplir les lotos, tournois et AG : inscriptions et bénévoles.',
    why: () => [
      { icon: '🎟', title: 'Tu sais qui vient, et à combien', text: 'Loto, tournoi, AG : sans sondage à part ni liste papier.' },
      { icon: '🔢', title: 'Les places limitées se gèrent seules', text: 'Premiers inscrits, premiers servis.' },
      { icon: '🙋', title: 'Bénévoles et lots au même endroit', text: 'Chaque événement a ses créneaux et ses appels aux partenaires.' },
      { icon: '📣', title: 'Annoncé en un geste', text: 'Aux membres, et sur la page du club si tu veux.' },
    ],
    setup: { min: 5, text: 'par événement : le créer et l’annoncer' },
    asks: 'Créer chaque événement et l’annoncer.',
    who: () => ['Tout le club'],
    appears: () => [
      ['Tout le club', 'L’agenda : tournois, lotos, AG ; s’inscrire en un geste. Le prochain événement sur l’accueil.'],
      ['Bureau', 'L’onglet Agenda : créer un événement, les inscrits, les places.'],
    ],
    hides: 'L’agenda du club et ses inscriptions.',
    keeps: 'Les événements et les inscrits : tout revient tel quel.',
  },
  avantages: {
    key: 'avantages', title: 'Avantages', short: 'Avantages', noun: 'les avantages', icon: 'ti-gift', goal: 'vivre', film: 'avantages',
    sub: () => 'Faire plaisir : offres des partenaires et cadeaux du club.',
    why: g => [
      { icon: '🎁', title: 'Dire merci sans dépenser plus', text: 'Une boisson, une gourde, une place au gala, au bon grade.' },
      { icon: '🏆', title: 'Une victoire se fête', text: `Un avantage pour ${agree(g, 'les joueurs présents', 'les joueuses présentes')} au match gagné.` },
      { icon: '🛍', title: 'Les offres des partenaires pour les familles', text: 'Le commerçant voit un écran qu’on ne copie pas.' },
      { icon: '🔒', title: 'Les partenaires voient ce qui a servi', text: 'Combien de fois, jamais par qui.' },
    ],
    setup: { min: 10, text: 'reprendre les 4 récompenses proposées ou écrire les tiennes' },
    asks: 'Choisir les récompenses et les honorer (buvette, boutique).',
    who: () => ['Membres adultes'],
    appears: () => [
      ['Membres', 'Moi › Mes avantages : les récompenses du club (et les offres des partenaires pour les adultes).'],
      ['Bureau', 'Les avantages à créer : limités, après une victoire, par grade.'],
    ],
    hides: 'Mes avantages, les récompenses des grades.',
    keeps: 'Les avantages et les récompenses : tout revient tel quel.',
  },
  defis: {
    key: 'defis', title: 'Défis et gages', short: 'Défis et gages', noun: 'les défis et gages', icon: 'ti-trophy', goal: 'vivre',
    sub: () => 'Garder les jeunes motivés : défis entre équipes, podiums, gages.',
    why: g => [
      { icon: '🔥', title: 'L’équipe vit entre deux entraînements', text: 'Défis, paliers, podium, objectif commun.' },
      { icon: '😂', title: 'Les gages soudent l’équipe', text: 'Toujours validés par le staff.' },
      { icon: '🏀', title: agree(g, 'Grands contre petits', 'Grandes contre petites'), text: 'Des défis entre catégories, avec la jauge de tout le club.' },
    ],
    setup: { min: 2, text: 'par défi, pour le coach ; rien pour le bureau' },
    asks: 'Rien : ce sont les coachs qui lancent.',
    who: g => ['Coachs', agree(g, 'Joueurs', 'Joueuses')],
    appears: g => [
      ['Coachs', 'Lancer un défi à l’équipe : paliers, podium, objectif commun ; les gages.'],
      [agree(g, 'Joueurs', 'Joueuses'), 'Les défis de leur équipe et leurs gages.'],
    ],
    hides: 'Les défis et les gages des équipes.',
    keeps: 'Les défis, podiums et gages : tout revient tel quel.',
  },
  prepa: {
    key: 'prepa', title: 'Prépa physique', short: 'Prépa physique', noun: 'la prépa physique', icon: 'ti-run', goal: 'jouer', film: 'prepa-coach',
    sub: g => `${agree(g, 'Des joueurs', 'Des joueuses')} en forme : programmes suivis et validés.`,
    why: () => [
      { icon: '💪', title: 'Tes équipes reprennent en forme', text: 'Un programme, la séance du jour sur le téléphone, trois niveaux.' },
      { icon: '✅', title: 'Le coach sait qui l’a faite', text: 'Et relance les retardataires en un geste.' },
      { icon: '🏅', title: 'Points, séries et classement', text: 'Ça donne envie, et la triche est impossible.' },
      { icon: '🏖', title: 'La séance continue hors du gymnase', text: 'Vacances, reprise, retour de blessure.' },
    ],
    setup: { min: 15, text: 'par programme, pour le coach' },
    asks: 'Rien : ce sont les coachs qui écrivent les programmes.',
    who: g => ['Coachs', agree(g, 'Joueurs', 'Joueuses')],
    appears: g => [
      ['Coachs', 'Les programmes de prépa de leurs équipes, le suivi.'],
      [agree(g, 'Joueurs', 'Joueuses'), 'La séance du jour sur l’accueil, validée en un geste.'],
      ['Parents', 'La séance du jour de leur enfant.'],
    ],
    hides: 'La prépa physique, la séance du jour, ses rappels.',
    keeps: 'Les programmes et les séances faites : tout revient tel quel.',
  },
  parrainage: {
    key: 'parrainage', title: 'Parrainage des jeunes', short: 'Parrainage', noun: 'le parrainage des jeunes', icon: 'ti-link', goal: 'vivre',
    sub: g => agree(g, 'Chaque grand suit un plus jeune : on reste au club.', 'Chaque grande suit une plus jeune : on reste au club.'),
    why: g => [
      { icon: '🤝', title: 'Chaque jeune a sa marraine ou son parrain', text: agree(g, 'Un grand du club qui le suit toute la saison.', 'Une grande du club qui la suit toute la saison.') },
      { icon: '💬', title: 'Les jeunes se sentent suivis', text: 'Un encouragement avant le match, un « J’y serai ».' },
      { icon: '🏠', title: 'Un club, pas une addition d’équipes', text: 'Les familles voient le lien entre les équipes.' },
      { icon: '⭐', title: agree(g, 'Tes grands s’engagent', 'Tes grandes s’engagent'), text: 'Chaque semaine d’encouragement compte comme une mission de bénévolat.' },
    ],
    setup: { min: 10, text: 'former les binômes' },
    asks: 'Former les binômes en début de saison, ou à une arrivée.',
    who: () => ['Bureau', 'Jeunes'],
    appears: () => [
      ['Bureau', 'Les binômes entre grands et jeunes, et les nouvelles.'],
      ['Jeunes', 'Leur marraine ou leur parrain : ses encouragements, son prochain match.'],
    ],
    hides: 'Le parrainage, ses nouvelles et ses encouragements.',
    keeps: 'Les binômes : tout revient tel quel.',
  },
  subventions: {
    key: 'subventions', title: 'Subventions', short: 'Subventions', noun: 'les subventions', icon: 'ti-bank', goal: 'financer', film: 'subventions', bureauOnly: true,
    sub: () => 'Ne plus rater une campagne ; des dossiers prêts à déposer.',
    why: () => [
      { icon: '📅', title: 'Tu ne rates plus une campagne', text: 'ANS, FDVA, mairie, département, région : rappel à J-30 et J-7.' },
      { icon: '🔢', title: 'Les chiffres du club sont prêts', text: 'Licenciés par âge et sexe, bénévoles, heures, km ; tu gardes la main.' },
      { icon: '🗂', title: 'Tes pièces resservent chaque année', text: 'Statuts, RIB, PV d’AG : déposés une seule fois.' },
      { icon: '📝', title: 'Fiche action guidée, CERFA prérempli', text: 'Tu relis, tu colles, tu déposes.' },
    ],
    setup: { min: 30, text: 'la première fois : les pièces au coffre et les chiffres du club ; le calendrier des campagnes est prêt' },
    asks: 'Relire et déposer toi-même sur Le Compte Asso ; tenir les pièces à jour.',
    who: () => ['Bureau'],
    appears: () => [
      ['Bureau', 'Les dossiers de subvention : calendrier, pièces, chiffres du club, fiche action.'],
      ['Membres', 'Rien : c’est l’affaire du bureau.'],
    ],
    hides: 'Les dossiers de subvention et leurs rappels.',
    keeps: 'Les pièces, les chiffres et les dossiers : tout revient tel quel.',
  },
  // D-123, D-130, D-138 : le planning des salles.
  salles: {
    key: 'salles', title: 'Planning des salles', short: 'Salles', noun: 'le planning des salles', icon: 'ti-building', goal: 'jouer',
    sub: () => 'Les créneaux du club sur un seul écran : la semaine, les imprévus, les vacances en 3 étapes.',
    why: () => [
      { icon: '🗓', title: 'La semaine du club en un coup d’œil', text: 'Chaque salle, chaque jour, chaque équipe : ce qui est libre, libéré, perdu ou en plus, et ce qui est à faire.' },
      { icon: '🏖', title: 'Les vacances en 3 étapes', text: 'Les créneaux habituels déjà remplis chez les coachs ; le responsable des salles n’arbitre que les ajouts.' },
      { icon: '⚠️', title: 'Un imprévu ne casse rien', text: 'Gymnase pris : la dérogation est préparée, le coach change de salle ou annule.' },
      { icon: '🥂', title: 'Le club-house sans SMS', text: 'Un pot, une réunion : tu acceptes en un geste.' },
    ],
    setup: { min: 30, text: 'la première saison : les salles reprises de la FFBB, les gestionnaires et les créneaux du club à noter ; ensuite, une fois par saison' },
    asks: 'Noter les créneaux une fois par saison ; répondre aux demandes (le message au gestionnaire est prêt).',
    who: () => ['Bureau', 'Responsable des salles', 'Coachs', 'Membres adultes'],
    appears: () => [
      ['Bureau', 'Les salles (la semaine du club, l’à-faire), les vacances en 3 étapes, créneau perdu ou en plus, les demandes de réception ; à la ⚙, les gestionnaires et les règles.'],
      ['Responsable des salles', 'Les mêmes écrans, même hors bureau : la personne de la fonction qui reçoit « Demandes de salle » (Club › Le bureau).'],
      ['Coachs', 'Leur semaine des salles, les créneaux libres à demander, leurs créneaux de vacances déjà remplis, la salle de chaque séance.'],
      ['Membres', '« Demander le club-house » pour un pot ou une réunion (adultes).'],
    ],
    hides: 'Les salles, leur planning et les demandes de réception ; le lieu d’une séance redevient un texte libre.',
    keeps: 'Les salles, les créneaux et les réservations : tout revient tel quel.',
  },
  // D-175, D-177 : le tournoi du club en direct ; il vit dans l'Agenda (un événement, son onglet « Tournoi »).
  tournois: {
    key: 'tournois', title: 'Tournois', short: 'Tournois', noun: 'les tournois', icon: 'ti-bracket', goal: 'vivre',
    sub: () => 'Le tournoi du club en direct : poules, scores des tables, TV de la salle, page à suivre sans compte.',
    why: g => [
      { icon: '🏆', title: 'Du tableur au tournoi publié', text: 'Le format proposé, les poules justes, le planning et la fin prévue en quelques minutes.' },
      { icon: '📱', title: 'Les scores viennent des tables', text: 'Un téléphone par terrain, autorisé par QR ; les capitaines confirment ou contestent.' },
      { icon: '📺', title: 'La salle suit en direct', text: 'La TV, la page du tournoi et « Mon équipe » sans compte : des scores, jamais un nom.' },
      { icon: '🧮', title: 'Le départage déjà fait', text: `Les règles FIBA 2026 en préréglage ; ${agree(g, 'les capitaines', 'les capitaines')} voient le classement en direct.` },
    ],
    setup: { min: 5, text: 'nommer les organisateurs ; chaque tournoi se crée ensuite dans l’Agenda' },
    asks: 'Créer chaque tournoi (quatre questions), valider les équipes, publier ; le jour J, trancher ce qui est contesté.',
    who: () => ['Bureau', 'Organisateurs', 'Coachs', 'Familles et public'],
    appears: () => [
      ['Bureau', '« + Tournoi » dans l’Agenda, l’onglet « Tournoi » de l’événement, le jour J une carte « Tournoi en direct » sur l’accueil.'],
      ['Organisateurs', 'Les mêmes écrans, nommés par le bureau.'],
      ['Coachs', 'Le tournoi interne de leur équipe, si tu le permets.'],
      ['Familles et public', 'La page du tournoi et « Mon équipe » sans compte ; la TV de la salle.'],
    ],
    hides: '« + Tournoi » et l’onglet « Tournoi » des événements ; les tournois déjà publiés restent lisibles par leur lien.',
    keeps: 'Les tournois, leurs équipes, leurs scores et les cartes règles du club : tout revient tel quel.',
  },
  // D-181 (précise D-150, D-154) : la boutique, trois façons de vendre, une page. Le club vend (HelloAsso), Arceau n'encaisse
  // rien (D-142) ; l'imprimeur vend dans sa propre boutique, Arceau la montre en vitrine.
  boutique: {
    key: 'boutique', title: 'Boutique', short: 'Boutique', noun: 'la boutique', icon: 'ti-bag', goal: 'financer',
    sub: () => 'Vendre aux couleurs du club : au club, en commande groupée, chez un imprimeur.',
    why: () => [
      { icon: '🛍', title: 'Une seule page Boutique', text: 'Dans l’app et sur le site : ce qui se retire au club, la commande groupée en cours, ce qui est livré chez soi.' },
      { icon: '👕', title: 'Les commandes groupées sans tableur', text: 'Tailles, flocage nom et numéro, paiement à la commande, le récapitulatif prêt pour l’équipementier.' },
      { icon: '🖨', title: 'Une boutique sans stock', text: 'Chez l’imprimeur : il fabrique, encaisse, livre et reprend ; le club touche sa marge.' },
      { icon: '💶', title: 'L’argent va au club', text: 'Paiement HelloAsso au nom du club, sans frais pour l’association ; Arceau ne touche rien.' },
    ],
    setup: { min: 10, text: 'choisir les rayons ; chez l’imprimeur, coller l’adresse de sa boutique' },
    asks: 'Une personne Boutique (choisie par le bureau) ajoute les articles, ouvre et clôt les commandes groupées, marque « Remis ».',
    who: () => ['Bureau', 'Personne Boutique', 'Familles et supporters', 'Visiteurs du site'],
    appears: () => [
      ['Tout le club', 'Club › Boutique : les rayons, le panier, « Mes commandes ». Les jeunes voient la page sans acheter : « Montrer à mon parent ».'],
      ['Personne Boutique', 'Trois onglets (Au club, Commandes groupées, Chez l’imprimeur) et « À faire ».'],
      ['Coachs', 'Les articles de leur équipe à remettre, quand la remise leur est confiée.'],
      ['Public', 'La page Boutique du site du club, sans aucun nom.'],
    ],
    hides: 'La page Boutique (app et site), le panier, les notifications de la boutique.',
    keeps: 'Les articles, les commandes, les remises et les réglages : tout revient tel quel si tu la rallumes.',
  },
  // D-152, D-182, D-183, D-184 : la caisse (le module « buvette », nommé « Caisse » depuis D-184) — la carte en ligne sans alcool, la caisse sur téléphone (hors ligne), le Z non
  // modifiable, les jetons du club, la tombola vendue sur place seulement.
  buvette: {
    key: 'buvette', title: 'Caisse', short: 'Caisse', noun: 'la caisse', icon: 'ti-cup', goal: 'financer',
    sub: () => 'La caisse de la buvette sur téléphone, le Z, les jetons du club et la tombola.',
    why: () => [
      { icon: '☕', title: 'La caisse sur un téléphone', text: 'La carte en tuiles, espèces, carte ou jetons ; elle marche même sans réseau.' },
      { icon: '🧾', title: 'Le Z sans calculette', text: 'Le comptage, l’écart, ce qui part à la banque : numéroté, non modifiable.' },
      { icon: '🪙', title: 'Les jetons du club', text: 'Un petit merci aux bénévoles et aux donateurs, à dépenser à la buvette ; hors banque.' },
      { icon: '🎟', title: 'La tombola tenue proprement', text: 'Vendue sur place seulement, papier ou téléphone, tirée en salle, suivie en direct.' },
    ],
    setup: { min: 10, text: 'la carte et ses prix, le fond de caisse, les jetons' },
    asks: 'Le bureau ou un responsable tient la carte, les autorisations temporaires et relit les Z ; le bénévole de la buvette tient la caisse.',
    who: () => ['Bureau', 'Responsables de la caisse', 'Bénévoles de la buvette', 'Tout le club', 'Visiteurs de la page du club'],
    appears: () => [
      ['Bureau', 'Club › Caisse : la carte, les autorisations, les Z, les jetons, la tombola.'],
      ['Bénévoles de la buvette', 'La caisse sur leur téléphone, pendant leur créneau « Buvette ».'],
      ['Tout le club', 'La carte et ses prix (sans alcool), « Mes jetons ».'],
      ['Public', 'La carte de la buvette sur la page du club, sans aucune boisson alcoolisée.'],
    ],
    hides: 'La caisse, la carte, les jetons et la tombola.',
    keeps: 'La carte, les Z, les jetons et les tombolas : tout revient tel quel si tu la rallumes.',
  },
  // D-152, D-182, D-185 : la Trésorerie du petit club ; le poste de travail du trésorier bénévole (mode « logiciel » sur
  // ordinateur, l'app au téléphone). Réservée au bureau ; un coach y voit le budget de son équipe.
  tresorerie: {
    key: 'tresorerie', title: 'Trésorerie', short: 'Trésorerie', noun: 'la trésorerie', icon: 'ti-treso', goal: 'financer', bureauOnly: true,
    sub: () => 'Une trésorerie juste et le dossier de l’AG prêt, sans ressaisir ce qu’Arceau sait déjà.',
    why: () => [
      { icon: '🏦', title: 'Le relevé rapproché au clavier', text: 'Import OFX ou CSV ; ce qu’Arceau attend passe tout seul : remises de chèques, HelloAsso, subventions, le Z de la buvette.' },
      { icon: '🧾', title: 'Les notes de frais photographiées', text: 'Le ticket pris en photo ; rembourser ou en faire don, avec le reçu fiscal.' },
      { icon: '📊', title: 'Le budget de chaque équipe', text: 'Voté à l’AG, suivi équipe par équipe ; chaque responsable voit le sien.' },
      { icon: '📁', title: 'Le dossier de l’AG en un geste', text: 'Comptes face au budget, trésorerie, bénévolat valorisé, rapport pré-rédigé, compte rendu 15059, FEC.' },
    ],
    setup: { min: 20, text: 'les dates de l’exercice, le seuil de la présidence, les comptes ; puis les soldes au 1er juillet et les relevés' },
    asks: 'La trésorerie (et son adjoint) tient la Trésorerie ; la présidence valide au-delà d’un seuil.',
    who: () => ['Trésorerie', 'Présidence', 'Bureau', 'Responsables d’équipe'],
    appears: () => [
      ['Trésorerie', 'La Trésorerie : sur ordinateur en mode « logiciel » (tableaux, raccourcis, export), au téléphone dans l’app.'],
      ['Présidence', 'Tout voir ; valider une dépense au-delà du seuil.'],
      ['Bureau', 'Le tableau de bord et le budget des équipes, sans rien de nominatif.'],
      ['Coachs', 'Le budget de leur équipe.'],
      ['Membres adultes', '« Mes dépenses pour le club » : une note de frais photographiée.'],
    ],
    hides: 'La Trésorerie, les notes de frais, le budget des équipes et leurs rappels.',
    keeps: 'Les écritures, les pièces, les budgets et les réglages : tout revient tel quel si tu la rallumes.',
  },
  // D-141 : la formation des officiels (bêta, club pilote) ; maquette f1b.
  formation: {
    key: 'formation', title: 'Formation', short: 'Formation', noun: 'la formation', icon: 'ti-grad', goal: 'mobiliser',
    beta: { label: 'Bêta · club pilote', price: 'Inclus dans l’abonnement' },
    sub: () => 'Former tes OTM et tes arbitres club : documents FFBB, simulateurs, vrais matchs, validés par le club.',
    why: () => [
      { icon: '🏅', title: 'Tenir tes obligations d’officiels', text: 'Le Pacte Officiels #TousEngagés de la FFBB demande aux clubs de repérer, former, suivre et valoriser leurs officiels : c’est fait, et ça se voit.' },
      { icon: '🎓', title: 'Former sans attendre une session', text: 'OTM et arbitres club apprennent sur les documents officiels de la FFBB, à leur rythme, sur leur téléphone.' },
      { icon: '🎯', title: 'S’entraîner sans pression', text: 'Deux simulateurs : la table de marque façon e-Marque, les gestes des arbitres. Ce n’est plus le jour du match qu’on découvre la table.' },
      { icon: '✅', title: 'Des officiels sûrs, reconnus', text: '3 matchs tenus pour de vrai, le feu vert d’un référent du club, puis un badge à côté de leur grade.' },
    ],
    setup: { min: 5, text: 'choisir les référents ; les parcours OTM et Arbitre club sont prêts' },
    asks: 'Rien au bureau. Les référents valident chaque officiel en un geste et peuvent ajouter une vidéo ou un document.',
    who: () => ['Bénévoles', 'Jeunes', 'Référents', 'Bureau'],
    appears: () => [
      ['Bénévoles', '« Formation » dans Club › Aider le club ; leur parcours sur l’accueil dès qu’ils l’ont commencé.'],
      ['Jeunes', 'Les mêmes parcours ; leurs résultats ne sont vus que de leur référent et de leurs parents.'],
      ['Référents', '« À valider » : les dossiers complets, à valider ou à renvoyer avec un mot.'],
      ['Bureau', '« Nos officiels » face aux matchs à domicile du mois, et la ⚙ des réglages.'],
    ],
    hides: 'La formation, les parcours sur l’accueil, « À valider » et « Nos officiels ».',
    keeps: 'Les progressions, les certifications et les réglages : tout revient tel quel.',
  },
  // D-149, D-154, D-157 : les skins, gagnés en utilisant l'app, pour tous ; allumé par défaut (DEFAULT_ON). Discrets : jamais
  // sur la page de vente (absent de MODULE_KEYS), rangés dans Moi › Apparence. La roue : les 10 skins que le club offre.
  skins: {
    key: 'skins', title: 'Skins', short: 'Skins', noun: 'les skins', icon: 'ti-palette', goal: 'vivre',
    sub: () => 'L’allure de l’app, gagnée en utilisant l’app, jamais en payant ; rangés dans Moi › Apparence.',
    why: () => [
      { icon: '🎨', title: 'Une app à son goût', text: 'Les couleurs, le fond, le cadre de la photo, l’animation de victoire.' },
      { icon: '📱', title: 'L’usage de l’app récompensé', text: 'Répondre, prévenir à temps, encourager, « Je viens voir », s’inscrire sur un créneau : jamais la présence, le talent ni l’argent.' },
      { icon: '🎁', title: 'Jusqu’à 10 skins offerts par le club', text: 'Avec tes conditions, à la roue de ce module ; personne n’en donne à la main.' },
    ],
    setup: { min: 1, text: 'rien à régler : déjà allumé pour le club' },
    asks: 'Rien : les skins se gagnent tout seuls ; si tu veux, tu en offres jusqu’à 10 avec tes conditions.',
    who: g => [agree(g, 'Joueurs', 'Joueuses'), 'Parents', 'Bénévoles', 'Supporters'],
    appears: g => [
      [agree(g, 'Joueurs', 'Joueuses'), 'Moi › Apparence › Mes skins ; le cadre de leur photo dans l’effectif de l’équipe.'],
      ['Parents, bénévoles, supporters', 'Moi › Apparence › Mes skins : les leurs.'],
      ['Coachs', 'Les cadres des photos dans l’effectif de leur équipe (leurs skins à eux : plus tard).'],
    ],
    hides: 'Les skins : chaque app revient aux couleurs du club, les cadres disparaissent de l’effectif.',
    keeps: 'Les skins gagnés, celui que chacun portait et les skins offerts par le club : tout revient tel quel si tu les rallumes.',
  },
};

/** Le socle, « Dans tous les cas, tu as déjà » (maquette m1) : ce que le club a sans rien activer. */
export const BASE_BENEFITS: Why[] = [
  { icon: '🏀', title: 'Toute la vie sportive, sans WhatsApp', text: 'Planning, convocations, présence par défaut, renforts, fiche de match.' },
  { icon: '📅', title: 'La FFBB suit toute seule', text: 'Calendrier, scores, classements, alertes, dérogations.' },
  { icon: '📋', title: 'Les outils des coachs', text: 'Plays animés, exercices, séances, chrono et drills.' },
  { icon: '📣', title: 'Tout le monde au courant', text: 'Annonces et sondages : qui a lu, qui a répondu.' },
];

// --- Activer selon les besoins (D-132, maquettes m1 et m2) -----------------------------------------------------------
export type NeedKey = 'benevoles' | 'argent' | 'licences' | 'esprit' | 'connaitre' | 'salles' | 'forme';
export interface Need {
  key: NeedKey;
  icon: string;
  /** « Qu'est-ce qui prend le plus de temps au bureau ? » : le choix. */
  title: (g: TeamGender) => string;
  /** Le titre de sa rubrique, « Pour le club : N modules ». */
  short: string;
  sub: string;
  modules: ModuleKey[];
}
export const NEEDS: Need[] = [
  { key: 'benevoles', icon: '🙋', title: () => 'Trouver des bénévoles pour les matchs', short: 'Trouver des bénévoles', sub: 'Table, chrono, buvette, toujours les mêmes', modules: ['benevolat'] },
  { key: 'argent', icon: '💶', title: () => 'Trouver de l’argent', short: 'Trouver de l’argent', sub: 'Partenaires, subventions, dons, boutique, buvette, les comptes', modules: ['partenaires', 'subventions', 'boutique', 'buvette', 'tresorerie'] },
  { key: 'licences', icon: '📋', title: () => 'Les licences et les dossiers', short: 'Les licences et les dossiers', sub: 'Pièces manquantes, paiements, relances', modules: ['licences'] },
  { key: 'esprit', icon: '🏀', title: () => 'Garder les jeunes et l’esprit club', short: 'Garder les jeunes et l’esprit club', sub: 'Défis, parrainage, événements, petits cadeaux',
    modules: ['defis', 'parrainage', 'avantages', 'agenda', 'tournois'] },
  { key: 'connaitre', icon: '📣', title: () => 'Faire connaître le club', short: 'Faire connaître le club', sub: 'Une page avec les matchs et les résultats', modules: ['page'] },
  { key: 'salles', icon: '🏟', title: () => 'Les salles et les créneaux', short: 'Les salles et les créneaux', sub: 'Qui joue où, les vacances, les imprévus', modules: ['salles'] },
  { key: 'forme', icon: '💪', title: g => agree(g, 'La forme des joueurs', 'La forme des joueuses'), short: 'La forme physique', sub: 'Programmes de prépa entre les séances', modules: ['prepa'] },
];

/** « Pour le club : N modules » : une rubrique par besoin coché, dans l'ordre des besoins ; un module n'y paraît qu'une
 *  fois (sous le premier besoin qui le demande) ; les minutes pour démarrer. */
export function proposal(picked: readonly NeedKey[]): { need: Need; modules: ModuleKey[]; min: number }[] {
  const seen = new Set<ModuleKey>();
  const out: { need: Need; modules: ModuleKey[]; min: number }[] = [];
  for (const need of NEEDS) {
    if (!picked.includes(need.key)) continue;
    const modules = need.modules.filter(k => !seen.has(k));
    modules.forEach(k => seen.add(k));
    if (modules.length) out.push({ need, modules, min: modules.reduce((n, k) => n + MODULES[k].setup.min, 0) });
  }
  return out;
}

/** Le bouton de l'activation : « Activer ces 4 modules », « Activer ce module » ; aucun : continuer sans. */
export const activateLabel = (n: number) => (n > 1 ? `Activer ces ${n} modules` : n === 1 ? 'Activer ce module' : 'Continuer sans module pour l’instant');
/** « 4 modules activés », « 1 module activé », « aucun module activé ». */
export const countLabel = (n: number) => (n > 1 ? `${n} modules activés` : n === 1 ? '1 module activé' : 'aucun module activé');
/** « 5 min », « 1 h 05 ». */
export const minutesLabel = (min: number) => (min >= 60 ? `${Math.floor(min / 60)} h${min % 60 ? ` ${String(min % 60).padStart(2, '0')}` : ''}` : `${min} min`);

/** Les pages d'un module (premier segment de la route). Le socle n'en a pas. `groupe` : l'ancienne route des défis
 *  (D-132 : « groupe » est réservé aux groupes de bénévoles ; les liens d'avant y mènent encore). */
const ROUTES: Record<string, ModuleKey> = {
  benevoles: 'benevolat', badges: 'benevolat', licences: 'licences', partenaires: 'partenaires', pistes: 'partenaires',
  prospection: 'partenaires', agenda: 'agenda', avantages: 'avantages', subventions: 'subventions', defis: 'defis', groupe: 'defis',
  prepa: 'prepa', marrainage: 'parrainage', suivis: 'page', salles: 'salles',
  lien: 'partenaires',   // Garder le lien (D-125) : un vieux lien, partenaires éteints, mène à l'écran du module (B10)
  formation: 'formation',   // D-141
  tournoi: 'tournois',   // D-175, D-177 : les écrans d'un tournoi (organisateurs)
  boutique: 'boutique',   // D-181 : la page Boutique, la gestion, la ⚙
  buvette: 'buvette',   // D-152, D-184 : la caisse, la carte, les jetons, la tombola, la ⚙
  tresorerie: 'tresorerie',   // D-182, D-185 : la Trésorerie du petit club
};

/** Le module d'une page (#/benevoles/…, #/club/page…), ou null pour le socle. `licences/import` reste au socle :
 *  importer les licenciés est un premier pas du bureau (l'effectif), même sans les dossiers. Club › Bénévolat (les
 *  réglages du bénévolat, D-127) est une page du module. */
export function moduleOfRoute(route: string[]): ModuleKey | null {
  const [a = '', b] = route;
  if (a === 'licences' && b === 'import') return null;
  if (a === 'club' && b === 'benevolat') return 'benevolat';
  if (a === 'moi' && b === 'trajets') return 'benevolat';   // Mes trajets pour le club : les frais des bénévoles (B24)
  if (a === 'moi' && b === 'skins') return 'skins';   // Mes skins (D-149)
  if (a === 'moi' && b === 'depenses') return 'tresorerie';   // Mes dépenses pour le club (D-185, P1)
  return ROUTES[a] ?? null;
}

/** Une notification ou une annonce : son module d'après la page où elle mène (comme la base, _notif_module). */
export function moduleOfLink(link: string | null | undefined): ModuleKey | null {
  if (!link?.startsWith('#/')) return null;
  return moduleOfRoute(link.slice(2).split('/'));
}

export type BureauTab = 'planning' | 'matchs' | 'equipes' | 'licences' | 'agenda' | 'benevoles';
/** Les onglets du milieu du bureau (entre Accueil et Club) : ceux de ses modules activés (Licences, Agenda,
 *  Bénévolat), complétés par le socle (Planning, Matchs, Équipes). Sans module : Planning · Matchs · Équipes ;
 *  avec les trois : comme avant, Licences · Agenda · Bénévolat. */
export function bureauTabs(on: (k: ModuleKey) => boolean): BureauTab[] {
  const mods: BureauTab[] = [];
  if (on('licences')) mods.push('licences');
  if (on('agenda')) mods.push('agenda');
  if (on('benevolat')) mods.push('benevoles');
  const base: BureauTab[] = ['planning', 'matchs', 'equipes'].slice(0, 3 - mods.length) as BureauTab[];
  return [...base, ...mods];
}

export interface PreviewNeed { label: string; needed: number }
/** « table de marque 1, chronomètre 1, buvette 2 ». */
export const needsLine = (needs: PreviewNeed[]) => needs.map(n => `${n.label.charAt(0).toLowerCase()}${n.label.slice(1)} ${n.needed}`).join(', ');

/** Ce que l'activation du bénévolat crée sur les matchs : « Les 3 matchs à domicile à venir demandent table de marque 1… (réglable). » */
export function matchesLine(n: number, needs: PreviewNeed[]): string {
  const what = needsLine(needs) || 'table de marque, chronomètre, buvette';
  if (!n) return `Les prochains matchs à domicile demanderont ${what} (réglable).`;
  return n > 1 ? `Les ${n} matchs à domicile à venir demandent ${what} (réglable).` : `Le match à domicile à venir demande ${what} (réglable).`;
}

/** La mise en place du bénévolat d'après les matchs à venir (maquette m4) : « les créneaux des 12 matchs à domicile se
 *  créent tout seuls ». */
export function volunteerSetup(homeMatches: number): string {
  if (homeMatches > 1) return `les créneaux des ${homeMatches} matchs à domicile se créent tout seuls`;
  if (homeMatches === 1) return 'les créneaux du match à domicile se créent tout seuls';
  return MODULES.benevolat.setup.text;
}

/** Ce que gardent les inscrits quand le bénévolat s'éteint : « Les 3 personnes déjà inscrites gardent leur créneau
 *  jusqu’à sa date (Karim, buvette samedi). » */
export function keptLine(n: number, first: { name: string; label: string; day: string } | null): string | null {
  if (!n) return null;
  const ex = first ? ` (${first.name}, ${first.label.charAt(0).toLowerCase()}${first.label.slice(1)} ${first.day})` : '';
  return n > 1 ? `Les ${n} personnes déjà inscrites gardent leur créneau jusqu’à sa date${ex}.` : `La personne déjà inscrite garde son créneau jusqu’à sa date${ex}.`;
}

/** Les récompenses des grades proposées à l'activation des Avantages (les mêmes que la base, _grade_rewards_defaults). */
export const DEFAULT_REWARDS: { grade: string; label: string; title: string }[] = [
  { grade: 'bronze', label: 'Bronze', title: 'Boisson offerte à la buvette' },
  { grade: 'argent', label: 'Argent', title: 'Gourde du club' },
  { grade: 'or', label: 'Or', title: 'Place au premier rang du gala' },
  { grade: 'legende', label: 'Légende', title: 'Maillot du club floqué' },
];

/** Les routes renommées par D-132 (vieux liens, notifications d'avant, favoris) : leur nouvelle adresse, ou null.
 *  « Défis et gages » : #/groupe… → #/defis… (« groupe » est réservé aux groupes de bénévoles) ; « Matchs du club » :
 *  #/matchs-club… → #/matchs… (un seul écran Matchs, tout le club, filtre par équipe). L'abonné (D-110) garde son
 *  accueil : ses liens #/matchs-club ne bougent pas. */
export function renamedRoute(hash: string, abonne = false): string | null {
  const h = hash.replace(/\/+$/, '');
  const defis = /^#\/groupe(\/.*)?$/.exec(h);
  if (defis) return `#/defis${defis[1] ?? ''}`;
  if (abonne) return null;
  if (h === '#/matchs-club') return '#/matchs';
  if (h === '#/matchs-club/resultats') return '#/matchs/resultats';
  return null;
}

/** La ligne « Modules » de la fiche d'un club dans la console (C1) : combien d'activés et lesquels (dans l'ordre de la
 *  base), puis ce que les coachs demandent au bureau. */
export function consoleModulesLine(rows: readonly { module: ModuleKey; enabled: boolean }[], asks: readonly { module: ModuleKey; name: string }[]):
  { title: string; sub: string; asked: number } {
  const on = ALL_MODULE_KEYS.filter(k => rows.some(r => r.module === k && r.enabled));
  const title = `Modules · ${on.length ? `${on.length} ${on.length > 1 ? 'activés' : 'activé'}` : 'aucun activé'}`;
  const byModule = new Map<ModuleKey, string[]>();
  for (const a of asks) byModule.set(a.module, [...(byModule.get(a.module) ?? []), a.name]);
  const asked = [...byModule].map(([k, names]) => `${names.slice(0, 2).join(' et ')}${names.length > 2 ? '…' : ''} ${names.length > 1 ? 'demandent' : 'demande'} ${MODULES[k].short}`);
  const sub = [on.length ? on.map(k => MODULES[k].short).join(', ') : 'Le socle seul', ...asked].join(' · ');
  return { title, sub, asked: byModule.size };
}
